"use client";

import { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axiosInstance from "../../../axiosInstance";
import { useTheme } from "@emotion/react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import DateRangeContext from "../../../contexts/Dashboard/DateRange";
import { useTranslation } from "react-i18next";

export default function ApexChart2() {
  const { startDate, endDate } = useContext(DateRangeContext);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  const selectedBranchesString = useSelector(
    (state) => state?.branches?.branchesString
  );

  const { t } = useTranslation();

  const lightMode = useSelector((state) => state.mode.lightMode);

  const theme = useTheme();

  const getSalesData = async () => {
    const formattedStartDate = startDate.format("YYYY-MM-DD");
    const formattedEndDate = endDate.format("YYYY-MM-DD");
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("accessToken");
      const token = localStorage.getItem("loginUser");
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_BASE_URL}dashboard/top_booked?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            XRefreshToken: `${token}`,
            Accounts: selectedBranchesString,
            "Content-Type": "application/json",
          },
        }
      );
      setLoading(false);
      const data = response.data.most_booked_residences;

      if (Object.keys(data).length) {
        const processedData = Object.entries(data).map(([name, value]) => ({
          name,
          value: Number(value),
        }));
        setChartData(processedData);
      } else {
        setChartData([{ name: t("noDataFound"), value: 0 }]);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      setChartData([{ name: "Error Loading Data", value: 0 }]);
    }
  };

  useEffect(() => {
    getSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedBranchesString, t]);

  // Özel tooltip bileşeni
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: lightMode ? "#fff" : "#333",
            border: `1px solid ${lightMode ? "#ccc" : "#666"}`,
            padding: "10px",
            borderRadius: "4px",
            color: lightMode ? "#333" : "#fff",
          }}
        >
          <p style={{ margin: 0 }}>{`${label}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  // Tarih aralığı başlığı
  const dateRangeTitle = `${dayjs(startDate).format("YYYY-MM-DD")} / ${dayjs(
    endDate
  ).format("YYYY-MM-DD")}`;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Typography
          variant="h5"
          color={lightMode ? theme.palette.customRed.orange : "#ffffff"}
          sx={{
            fontWeight: "700",
            mr: "auto",
            width: "100%",
            p: 2,
            borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`} `,
            textTransform: 'uppercase'
          }}
        >
          {t("topBookedResidences")}
        </Typography>
      </Box>
      {loading ? (
        <CircularProgress sx={{ margin: "auto" }} />
      ) : (
        <Box
          id="chart"
          sx={{ mt: "auto", mb: "auto", width: "100%", height: 350, p: 2 }}
        >
          <Typography
            variant="body1"
            color={lightMode ? "text.secondary" : "text.primary"}
            sx={{ mb: 2, textAlign: "start", fontWeight: "700" }}
          >
            {dateRangeTitle}
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 30,
                right: 30,
                left: 30,
                bottom:
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? 100
                    : 30,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={lightMode ? "#e0e0e0" : "#444"}
              />
              <XAxis
                dataKey="name"
                angle={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? -45
                    : 0
                }
                textAnchor={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? "end"
                    : "middle"
                }
                tick={{
                  fill: lightMode ? "#919eab" : "#ffffff",
                  fontSize: 12,
                }}
                tickMargin={10}
                height={
                  chartData.reduce((acc, cur) => acc + cur.name.length, 0) > 80
                    ? 70
                    : 40
                }
                stroke={lightMode ? "#919eab" : "#ffffff"}
              />

              <YAxis
                tick={{ fill: lightMode ? "#444444" : "#ffffff", fontSize: 12 }}
                stroke={lightMode ? "#444444" : "#ffffff"}
              />
              <Tooltip content={<CustomTooltip />} />
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.dark} />
                  <stop offset="50%" stopColor={theme.palette.primary.light} />
                  <stop offset="100%" stopColor={"#496690"} />
                </linearGradient>
              </defs>

              <Bar
                dataKey="value"
                fill={lightMode ? "url(#barGradient)" : "#ffffff"}
                barSize={20}
                radius={[10, 10, 0, 0]}
                animationDuration={1000}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}
